import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/Header";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DeclineDeliveryDialog } from "@/components/DeclineDeliveryDialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getNotificationPath } from "@/lib/notificationTarget";

type NotificationRow = {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  dataJson?: {
    orderId?: string;
    deliveryRequestId?: string;
    [key: string]: unknown;
  } | null;
  isRead: number;
  createdAt: string;
};

function formatWhen(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default function Notifications() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [declineTargetId, setDeclineTargetId] = useState<string | null>(null);

  const { data: notifications = [], isLoading } = useQuery<NotificationRow[]>({
    queryKey: ["/api/my/notifications"],
    queryFn: async () => {
      const res = await fetch("/api/my/notifications", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const unreadCount = useMemo(
    () => notifications.filter((notification) => Number(notification.isRead) !== 1).length,
    [notifications]
  );

  const readOneMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/my/notifications/${id}/read`);
      return res.json() as Promise<NotificationRow>;
    },
    onSuccess: (updatedNotification) => {
      queryClient.setQueryData<NotificationRow[]>(["/api/my/notifications"], (current = []) =>
        current.map((notification) =>
          notification.id === updatedNotification.id
            ? { ...notification, isRead: 1 }
            : notification
        )
      );
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications/unread-count"] });
    },
    onError: (error: any) => {
      toast({
        title: "Could not mark notification as read",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const readAllMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/my/notifications/read-all");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications/unread-count"] });
    },
  });

  const resolveCancellationMutation = useMutation({
    mutationFn: async ({
      orderId,
      status,
      notificationId,
    }: {
      orderId: string;
      status: "refunded" | "paid";
      notificationId: string;
    }) => {
      trackEvent("order_action_tap", "/notifications", {
        orderId,
        action: status,
        surface: "notifications",
      });
      const res = await apiRequest("POST", `/api/orders/${orderId}/status`, { status });
      const data = await res.json();
      try {
        await apiRequest("POST", `/api/my/notifications/${notificationId}/read`);
      } catch (markReadError) {
        console.warn("Could not mark notification as read after resolve", markReadError);
      }
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/notifications/unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/orders"] });
      if (variables.status !== "refunded") {
        toast({ title: "Order kept active" });
        return;
      }
      const refund = (data as any)?.refund as
        | { attempted: boolean; issued: boolean; error: string | null; refundId: string | null }
        | undefined;
      if (refund?.attempted && refund.issued) {
        toast({
          title: "Cancellation approved · refund issued",
          description: refund.refundId
            ? `Stripe refund ${refund.refundId} sent to the buyer.`
            : "Refund sent to the buyer via Stripe.",
        });
      } else if (refund?.attempted) {
        toast({
          title: "Cancellation approved, refund failed",
          description: `Order was cancelled but Stripe did not confirm the refund${refund.error ? `: ${refund.error}` : ""}. Issue the refund manually from your Stripe dashboard.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "Cancellation approved" });
      }
    },
    onError: (error: any, variables) => {
      trackEvent("order_action_fail", "/notifications", {
        orderId: variables.orderId,
        action: variables.status,
        surface: "notifications",
        message: String(error?.message || "").slice(0, 300),
      });
      toast({
        title: "Could not update cancellation",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
            <p className="text-sm text-muted-foreground">
              Order updates and activity related to your account.
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            disabled={unreadCount === 0 || readAllMutation.isPending}
            onClick={() => readAllMutation.mutate()}
          >
            Mark all read
          </Button>
        </div>

        <div className="text-sm text-muted-foreground">
          {unreadCount} unread
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="py-6 text-sm text-muted-foreground">
              Loading notifications…
            </CardContent>
          </Card>
        ) : notifications.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-sm text-muted-foreground">
              No notifications yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const isRead = Number(notification.isRead) === 1;
              const orderId = notification.dataJson?.orderId;
              const isDeliveryOffer =
                notification.type === "delivery_request_created" ||
                notification.type === "delivery_signup_invite";
              const deliveryRequestId = isDeliveryOffer
                ? (notification.dataJson?.deliveryRequestId as string | undefined)
                : undefined;
              const acceptHref = deliveryRequestId
                ? `/deliveries?accept=${encodeURIComponent(deliveryRequestId)}`
                : "/deliveries";
              const targetPath = isDeliveryOffer
                ? null
                : getNotificationPath(notification.type, notification.dataJson);
              const cardClickable = targetPath != null;

              const handleCardClick = () => {
                if (!cardClickable || !targetPath) return;
                if (!isRead) readOneMutation.mutate(notification.id);
                setLocation(targetPath);
              };

              return (
                <Card
                  key={notification.id}
                  className={[
                    isRead ? "opacity-85" : "border-primary/30 shadow-sm",
                    cardClickable ? "cursor-pointer transition-colors hover:border-primary/60" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={cardClickable ? handleCardClick : undefined}
                  role={cardClickable ? "button" : undefined}
                  tabIndex={cardClickable ? 0 : undefined}
                  onKeyDown={
                    cardClickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick();
                          }
                        }
                      : undefined
                  }
                >
                  <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                    <div className="space-y-1">
                      <CardTitle className="text-base">{notification.title}</CardTitle>
                      <div className="text-xs text-muted-foreground">
                        {formatWhen(notification.createdAt)}
                      </div>
                    </div>

                    {isRead ? (
                      <Badge variant="secondary">Read</Badge>
                    ) : (
                      <Badge>Unread</Badge>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <p className="text-sm text-foreground/90">{notification.body}</p>

                    <div
                      className="flex flex-wrap gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {notification.type === "cancellation_requested" && orderId && !isRead && (
                        <>
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            disabled={resolveCancellationMutation.isPending}
                            onClick={() =>
                              resolveCancellationMutation.mutate({
                                orderId,
                                status: "refunded",
                                notificationId: notification.id,
                              })
                            }
                          >
                            Approve cancellation &amp; refund
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={resolveCancellationMutation.isPending}
                            onClick={() =>
                              resolveCancellationMutation.mutate({
                                orderId,
                                status: "paid",
                                notificationId: notification.id,
                              })
                            }
                          >
                            Keep order active
                          </Button>
                        </>
                      )}

                      {isDeliveryOffer && !isRead && deliveryRequestId && (
                        <>
                          <Button asChild type="button" variant="default" size="sm">
                            <Link href={acceptHref}>
                              {notification.type === "delivery_signup_invite"
                                ? "Sign up & accept"
                                : "Accept"}
                            </Link>
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setDeclineTargetId(deliveryRequestId)}
                            data-testid={`decline-delivery-${deliveryRequestId}`}
                          >
                            Decline
                          </Button>
                        </>
                      )}

                      {!isRead && !isDeliveryOffer && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={readOneMutation.isPending}
                          onClick={() => readOneMutation.mutate(notification.id)}
                        >
                          Mark read
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <DeclineDeliveryDialog
        open={declineTargetId != null}
        onOpenChange={(open) => !open && setDeclineTargetId(null)}
        deliveryRequestId={declineTargetId}
        onDeclined={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
          queryClient.invalidateQueries({ queryKey: ["/api/my/notifications/unread-count"] });
        }}
      />
    </div>
  );
}
